import React from 'react'
import {Link} from '@mui/material'
import dayjs from 'dayjs'
import {StyledAIInfoBox} from './ChatbotStyles'
import {isColumnNumeric} from '../../shared/helpers/tableUtils'
import {getPrismProgram} from '../../shared/helpers/common'

const MAX_SESSION_STORAGE_SIZE = 5 * 1024 * 1024

export const customStyles = {
  orderedListMargin: {margin: '5px 0px'},
  csvDisplay: {display: 'none'}
}

export const getSessionStorageSizeExceptMessages = () => {
  let totalSize = 0
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    const value = sessionStorage.getItem(key)
    if (key !== 'messages') {
      totalSize += key.length + value.length
    }
  }
  return totalSize
}

export const getParsedData = (jsonString) => {
  try {
    return JSON.parse(jsonString)
  } catch {
    return null
  }
}

export const checkMaxLimitIsReached = (messages) => {
  if (!messages || !Array.isArray(messages)) {
    return false
  }
  const bufferSizeLimit = 256000 // 250KB
  const currentSizeExceptMessages = getSessionStorageSizeExceptMessages()
  return (
    currentSizeExceptMessages + JSON.stringify(messages).length >
    Math.floor((MAX_SESSION_STORAGE_SIZE - currentSizeExceptMessages) / 2) - bufferSizeLimit
  )
}

const isMessagesAndHasArray = (key, value) => {
  return key === 'messages' && Array.isArray(value)
}

export const setSessionData = (key, value) => {
  const bufferSize = 51200 // 50KB
  const currentSize = getSessionStorageSizeExceptMessages()
  const availableSpace = Math.floor((MAX_SESSION_STORAGE_SIZE - currentSize) / 2) - bufferSize
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
    if (isMessagesAndHasArray(key, value) && JSON.stringify(value).length + key.length > availableSpace) {
      throw new Error('Limit Exceeded for messages')
    }
  } catch {
    if (key === 'messages' && Array.isArray(value)) {
      let shouldUpdate = false
      const messages = [...value]
      while (JSON.stringify(messages).length + key.length > availableSpace && messages.length > 0) {
        messages.splice(0, 1)
        shouldUpdate = true
      }
      if (shouldUpdate) {
        sessionStorage.setItem(key, JSON.stringify(messages))
        return {
          shouldUpdate,
          data: messages
        }
      }
    }
  }
}

export const getSessionData = (key) => {
  return sessionStorage.getItem(key)
}

export const shouldDisplayInfoText = (messages, hideWarningBanner) => {
  if (!messages || !Array.isArray(messages)) {
    return false
  }
  const hasPrompts = messages.find((msgObj) => msgObj.isPrompt)
  return !hasPrompts && checkMaxLimitIsReached(messages) && !hideWarningBanner
}

export const getFormattedTimestamp = () => {
  return dayjs().format(`MM_DD_YYYY_hh_mm_ssA`)
}

export const getFormattedName = (name) => {
  const nameArr = name?.split(', ') || []
  return nameArr.length === 2 ? `${nameArr[1]}` : name
}

export const getChartColors = (trace) => {
  const dColors = [
    '#0097A9',
    '#ED8B00',
    '#86BC25',
    '#62B5E5',
    '#A497F4',
    '#FFA0C5',
    '#009A44',
    '#005587',
    '#FFCD00',
    '#BBC99B'
  ]
  if (trace?.type === 'scatter' && trace?.mode === 'lines') {
    return {
      ...trace,
      line: {
        ...trace?.line,
        color: dColors[0]
      }
    }
  } else if (trace?.type === 'pie') {
    return {
      ...trace,
      marker: {
        ...trace?.marker,
        colors: dColors
      }
    }
  } else {
    return {
      ...trace,
      marker: {
        ...trace?.marker,
        color: dColors[0]
      }
    }
  }
}

export const shouldDisableButtons = (messages, showLoader) => {
  const hasPrompts = messages.find((msgObj) => msgObj.isPrompt)
  return !!hasPrompts || showLoader
}

export const getFormattedAIInfoText = (infoText) => {
  if (infoText?.includes('FAQ')) {
    const fragments = infoText.split('FAQ')
    return (
      <StyledAIInfoBox tabIndex={0}>
        {fragments[0]}
        <Link href='/faq'>FAQ</Link>
        {fragments[1]}
      </StyledAIInfoBox>
    )
  }
  return <StyledAIInfoBox tabIndex={0}>{infoText}</StyledAIInfoBox>
}

export const getFormattedSources = (sources, id) => {
  if (sources) {
    const sourcesArr = sources.split(/\{([^{}]*)\}/g)
    if (sourcesArr?.length > 1) {
      const tableReferences = sourcesArr[1]?.split(', ')
      const sourceList = tableReferences.map((source) => {
        return source.slice(1, -1)
      })
      return (
        <div>
          <div>{sourcesArr[0]?.trim()}:</div>
          <ol style={customStyles.orderedListMargin}>
            {sourceList.map((sourceName) => (
              <li key={sourceName + '-' + id}>{sourceName}</li>
            ))}
          </ol>
        </div>
      )
    }
  }
  return sources
}

const formatMarkdownTable = (tableText) => {
  const lines = tableText
    .trim()
    .split('\n')
    .filter((line) => line.trim() !== '')

  // Find the header line (first line)
  const headerLine = lines[0]

  // Find the separator line (should contain dashes and pipes)
  let separatorIndex = -1
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].includes('-') && lines[i].includes('|')) {
      separatorIndex = i
      break
    }
  }

  // If no separator found, treat all lines after first as data
  const dataStartIndex = separatorIndex === -1 ? 1 : separatorIndex + 1
  const dataLines = lines.slice(dataStartIndex)

  // Parse header and clean up escaped characters
  const headers = headerLine
    .split('|')
    .map((cell) => cell.trim().replaceAll(String.raw`\_`, '_')) // Fix escaped underscores
    .filter((cell) => cell !== '')

  // Parse data rows and clean up escaped characters
  const rows = dataLines
    .filter((line) => line.includes('|')) // Only process lines that contain pipes
    .map((line) =>
      line
        .split('|')
        .map((cell) => cell.trim().replaceAll(String.raw`\_`, '_')) // Fix escaped underscores
        .filter((cell) => cell !== '')
    )
    .filter((row) => row.length > 0) // Remove empty rows

  const tableStyles = {
    borderCollapse: 'collapse',
    width: '100%',
    marginTop: '8px',
    marginBottom: '8px',
    fontSize: 'inherit',
    tableLayout: 'fixed'
  }

  const headerStyles = {
    fontWeight: '600',
    padding: '8px 12px',
    textAlign: 'left',
    borderBottom: '1px solid #ddd',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }

  const getCellStyles = (cellContent, columnIndex, rowIndex, totalRows) => {
    const isNumeric = /^[\d,.-]+$/.test(cellContent?.toString().trim())
    const isLastRow = rowIndex === totalRows - 1

    return {
      padding: '6px 12px',
      textAlign: isNumeric ? 'right' : 'left',
      verticalAlign: 'top',
      borderRight: columnIndex < headers.length - 1 ? '1px solid #eee' : 'none',
      borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '13px'
    }
  }

  return (
    <table style={tableStyles}>
      <thead>
        <tr>
          {headers.map((header, index) => (
            <th
              key={`header-${header}-${index}`}
              style={{
                ...headerStyles,
                width: index === 0 ? '40%' : `${60 / (headers.length - 1)}%`,
                borderRight: index < headers.length - 1 ? '1px solid #eee' : 'none',
                textAlign: isColumnNumeric(rows, index) ? 'right' : 'left'
              }}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`row-${row.join('-')}-${rowIndex}`}>
            {row.map((cell, cellIndex) => (
              <td
                key={`cell-${cell}-${rowIndex}-${cellIndex}`}
                style={getCellStyles(cell, cellIndex, rowIndex, rows.length)}
                title={cell} // Add tooltip for overflow text
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const detectAndFormatTables = (text) => {
  // Improved regex to detect complete markdown tables
  // Matches: header row, separator row, and all data rows
  const tableRegex = /^\|.*\|$\n^\|[-\s|:]+\|$\n(^\|.*\|$\n?)+/gm

  const result = []
  let lastIndex = 0
  let match

  while ((match = tableRegex.exec(text)) !== null) {
    // Add text before the table
    if (match.index > lastIndex) {
      const beforeTableText = text.substring(lastIndex, match.index).trim()
      if (beforeTableText) {
        result.push(beforeTableText)
      }
    }

    result.push(formatMarkdownTable(match[0]))
    lastIndex = match.index + match[0].length
  }

  // Add remaining text after the last table
  if (lastIndex < text.length) {
    const afterTableText = text.substring(lastIndex).trim()
    if (afterTableText) {
      result.push(afterTableText)
    }
  }

  // If no tables were found, return the original text
  if (result.length === 0) {
    return text
  }

  return result
}

export const handleTextFormatting = (text) => {
  if (!text) {
    return text
  }

  // First check if the text contains tables
  const hasTable = /^\|.*\|$\n^\|[-\s|:]+\|$\n(^\|.*\|$\n?)+/m.test(text)

  if (hasTable) {
    const parts = detectAndFormatTables(text)
    return (
      <div>
        {parts.map((part) => {
          if (React.isValidElement(part)) {
            // This is a table component
            return <div key={getRandomId()}>{part}</div>
          } else {
            // This is regular text, apply normal formatting
            let formattedText = part.replaceAll(/\*\*(.*?)\*\*/g, `<span style='font-weight:600'>$1</span>`)
            formattedText = formattedText?.endsWith('\n') ? formattedText.slice(0, -1) : formattedText

            return formattedText.includes('\n') ? (
              <div key={getRandomId()}>
                {formattedText.split('\n').map((line) => (
                  <div
                    key={getRandomId()}
                    dangerouslySetInnerHTML={{__html: line || `<div style='height: 12px'></div>`}}
                  />
                ))}
              </div>
            ) : (
              <div key={getRandomId()} dangerouslySetInnerHTML={{__html: formattedText}} />
            )
          }
        })}
      </div>
    )
  }

  // Original formatting for non-table text
  let formattedText = text.replaceAll(/\*\*(.*?)\*\*/g, `<span style='font-weight:600'>$1</span>`)
  formattedText = formattedText?.endsWith('\n') ? formattedText.slice(0, -1) : formattedText
  return formattedText.includes('\n') ? (
    formattedText
      .split('\n')
      .map((line) => (
        <div key={getRandomId()} dangerouslySetInnerHTML={{__html: line || `<div style='height: 12px'></div>`}} />
      ))
  ) : (
    <div dangerouslySetInnerHTML={{__html: formattedText}} />
  )
}

export const getRandomId = (prefix = '') => {
  return (prefix + 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx').replaceAll(/[xy]/g, function (c) {
    const crypto = globalThis.crypto || globalThis.msCrypto
    const array = new Uint8Array(1)
    crypto.getRandomValues(array)
    const r = array[0] % 16
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const getGuidingPrompts = () => {
  return [...getGeneralInquiryGuidingPrompts(), ...getDataInquiryGuidingPrompts()]
}

export const getAIInfoText = () => {
  return 'This response was generated using AI technology for informational purposes only.'
}

export const getEllipsis = (text, length = 85) => {
  return text?.length > length ? text.slice(0, length) + '...' : text
}

export const getGeneralInquiryGuidingPrompts = () => {
  const assistant = getPrismProgram()
  return [
    {
      id: getRandomId(),
      type: 'Bot',
      label: `${assistant} Agent`,
      responseText: `Welcome! I'm ${assistant} Agent and I'm here to answer your general questions and help you navigate the ${assistant} data dictionary. You can ask me about specific data variables, their descriptions, and other common questions about ${assistant} (between 4 and 50 words). How can I assist you today?`
    },
    {
      id: getRandomId(),
      type: 'User',
      label: 'Get Started',
      message: 'Provide an overview of the income variable.',
      isPrompt: true
    },
    {
      id: getRandomId(),
      type: 'User',
      message: `What use cases can ${assistant} address?`,
      isPrompt: true
    }
  ]
}

export const getDataInquiryGuidingPrompts = () => {
  const assistant = getPrismProgram()
  return [
    {
      id: getRandomId(),
      type: 'Bot',
      label: `${assistant} Agent`,
      responseText: `Welcome! I'm ${assistant} Agent and I can help you better understand your populations of interest, by providing the insights and visuals you need. Leverage one of the largest Community Factors dataset to get actionable social, economic, health, and lifestyle insights on adults across the United States.\n\nTo explore ${assistant} data, ask me a question specifying the geography, Social Driver variable, health condition, and desired output (between 4 and 50 words). How can I assist you today?`
    },
    {
      id: getRandomId(),
      type: 'User',
      label: 'Get Started',
      message: 'How many Texas households are at the 90th percentile or higher for asthma risk?',
      isPrompt: true
    },
    {
      id: getRandomId(),
      type: 'User',
      message: 'List the top personas in Pennsylvania who are SNAP eligible.',
      isPrompt: true
    }
  ]
}

export const getAPIFailureResponse = (prismProgram) => {
  return {
    id: getRandomId(),
    type: 'Bot',
    label: `${prismProgram} Agent`,
    responseText: "I'm experiencing some difficulties retrieving the information. Please try again shortly."
  }
}
export const getAPIInterruptedResponse = (prismProgram) => {
  return {
    id: getRandomId(),
    type: 'Bot',
    label: `${prismProgram} Agent`,
    responseText: 'There was an interruption while generating the response. Please try again shortly.'
  }
}
export const generateAPIResponse = (prismProgram, isInterrupted = false) => {
  if (isInterrupted) {
    return getAPIInterruptedResponse(prismProgram)
  }
  return getAPIFailureResponse(prismProgram)
}

// SSE Stream Processing Functions
export const processSSELines = (lines, tempResponseId, accumulatedText, processStreamDataCallback) => {
  let updatedAccumulatedText = accumulatedText

  for (const line of lines) {
    if (line.trim().startsWith('data: ')) {
      try {
        const jsonStr = line.substring(6).trim()
        if (jsonStr && jsonStr !== '[DONE]') {
          const data = JSON.parse(jsonStr)
          updatedAccumulatedText = processStreamDataCallback(data, tempResponseId, updatedAccumulatedText)
        }
      } catch (parseError) {
        console.log('Error parsing SSE data:', parseError, 'Line:', line)
      }
    }
  }

  return updatedAccumulatedText
}

// Extract text content from streaming data parts
const extractTextContent = (data, accumulatedText) => {
  let newText = ''

  // Skip function call/response chunks — they don't contain displayable text
  const hasFunctionContent = data.content?.parts?.some((part) => part.functionCall || part.functionResponse)
  if (hasFunctionContent) {
    return newText
  }

  if (data.partial === true && data.content?.parts) {
    for (const part of data.content.parts) {
      if (part.text) {
        newText += part.text
      }
    }
  }
  // Handle case where partial field is missing but content parts exist (Response #1 final chunk)
  if (!Object.hasOwn(data, 'partial') && data.content?.parts && accumulatedText?.trim() === '') {
    newText = data.content.parts
      .map((part) => part?.text)
      .filter(Boolean)
      .join('')
  }
  // Handle partial: false with full text as fallback when no text was accumulated via streaming
  if (data.partial === false && data.content?.parts?.some((part) => part.text) && accumulatedText?.trim() === '') {
    newText = data.content.parts
      .map((part) => part?.text)
      .filter(Boolean)
      .join('')
  }
  return newText
}

// Track streaming states per message to handle partial field variations
const streamingStates = {}

// Initialize or get streaming state for response ID
export const initializeStreamState = (tempResponseId) => {
  if (!streamingStates[tempResponseId]) {
    streamingStates[tempResponseId] = {
      hasSeenPartial: false,
      chunkCount: 0,
      completed: false
    }
  }
  return streamingStates[tempResponseId]
}

// Check if streaming should complete based on data and state
export const shouldCompleteStream = (data, streamState) => {
  // Skip completion for function call/response chunks — stream continues after these
  const hasFunctionCall = data.content?.parts?.some((part) => part.functionCall || part.functionResponse)
  if (hasFunctionCall) {
    return false
  }

  // Skip if stream already completed (prevents double-completion from trailing chunks)
  if (streamState.completed) {
    return false
  }

  // 1. Explicit partial: false on a text content chunk
  const isExplicitEnd =
    Object.hasOwn(data, 'partial') && data.partial === false && data.content?.parts?.some((part) => part.text)

  // 2. No partial field AND we've seen partial: true before (final chunk pattern in Response #1)
  // AND has usage metadata with candidatesTokenCount AND has text content
  const isFinalChunkAfterStreaming =
    streamState.hasSeenPartial &&
    !Object.hasOwn(data, 'partial') &&
    data.content?.parts?.some((part) => part.text) &&
    data.usageMetadata?.candidatesTokenCount

  return isExplicitEnd || isFinalChunkAfterStreaming
}

// Handle empty response error case
export const handleEmptyResponse = (callbacks, tempResponseId) => {
  const {
    hideChatLoader,
    setIsRequestActive,
    setActiveRequestId,
    setMessages,
    populateErrorResponse,
    processingRequestRef
  } = callbacks

  hideChatLoader()
  if (setIsRequestActive) {
    setIsRequestActive(false)
  }
  if (setActiveRequestId) {
    setActiveRequestId(null)
  }
  // Reset processing flag immediately when stream logically completes (even with empty response)
  if (processingRequestRef) {
    processingRequestRef.current = false
  }

  // Remove the empty streaming message and show error
  setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== tempResponseId))

  // Show error message for empty response
  if (populateErrorResponse) {
    populateErrorResponse({
      response: ['Oops, something went wrong on our end. Please try again in a few moments.']
    })
  }

  // Clean up streaming state
  delete streamingStates[tempResponseId]
}

// Handle successful stream completion
export const handleSuccessfulCompletion = (callbacks, tempResponseId) => {
  const {hideChatLoader, setIsRequestActive, setActiveRequestId, setMessages, processingRequestRef} = callbacks

  hideChatLoader()
  if (setIsRequestActive) {
    setIsRequestActive(false)
  }
  if (setActiveRequestId) {
    setActiveRequestId(null)
  }
  // Reset processing flag immediately when stream logically completes
  if (processingRequestRef) {
    processingRequestRef.current = false
  }
  setMessages((prevMessages) =>
    prevMessages.map((msg) => (msg.id === tempResponseId ? {...msg, isStreaming: false} : msg))
  )

  // Clean up streaming state
  delete streamingStates[tempResponseId]
}

// Update hasSeenPartial tracking for text-only partial chunks
const updatePartialTracking = (data, streamState) => {
  const hasTextContent = data.content?.parts?.some((part) => part.text)
  const hasFunctionContent = data.content?.parts?.some((part) => part.functionCall || part.functionResponse)
  if (Object.hasOwn(data, 'partial') && data.partial === true && hasTextContent && !hasFunctionContent) {
    streamState.hasSeenPartial = true
  }
}

// Resolve the final text from accumulated text or fallback to chunk content
const resolveFinalText = (accumulatedText, data) => {
  if (accumulatedText?.trim()) {
    return accumulatedText
  }
  if (!data.content?.parts) {
    return ''
  }
  return data.content.parts
    .map((part) => part?.text)
    .filter(Boolean)
    .join('')
}

// Update message when accumulated text was empty but final chunk has text
const updateMessageWithFinalText = (accumulatedText, finalText, callbacks, tempResponseId) => {
  if (accumulatedText?.trim() || !finalText) return
  const {setMessages, handleAutoScrollToBottom} = callbacks
  setMessages((prevMessages) =>
    prevMessages.map((msg) => (msg.id === tempResponseId ? {...msg, responseText: finalText} : msg))
  )
  if (handleAutoScrollToBottom) handleAutoScrollToBottom()
}

// Handle stream completion state management
export const handleStreamCompletion = (data, callbacks, tempResponseId, accumulatedText) => {
  const {hideChatLoader} = callbacks

  if (!hideChatLoader) return

  const streamState = initializeStreamState(tempResponseId)
  streamState.chunkCount++

  updatePartialTracking(data, streamState)

  // Skip if already completed (trailing chunks like coordination_state updates)
  if (streamState.completed) return

  if (!shouldCompleteStream(data, streamState)) return

  // Mark as completed to prevent re-triggering from trailing chunks
  streamState.completed = true

  const finalText = resolveFinalText(accumulatedText, data)

  if (!finalText || finalText.trim() === '') {
    handleEmptyResponse(callbacks, tempResponseId)
    return
  }

  updateMessageWithFinalText(accumulatedText, finalText, callbacks, tempResponseId)
  handleSuccessfulCompletion(callbacks, tempResponseId)
}

// Extract SQL-related data from stream
export const extractSqlData = (data) => {
  let sqlQuery = null
  let sqlExecutionResult = null

  if (data.actions?.stateDelta?.sql_query) {
    sqlQuery = data.actions.stateDelta.sql_query
  } else if (data.actions?.stateDelta?.executed_sql) {
    sqlQuery = data.actions.stateDelta.executed_sql
  } else if (data.actions?.stateDelta?.generated_sql) {
    sqlQuery = data.actions.stateDelta.generated_sql
  }

  if (data.actions?.stateDelta?.sql_execution_result) {
    sqlExecutionResult = data.actions.stateDelta.sql_execution_result
  }

  return {sqlQuery, sqlExecutionResult}
}

// Extract Plotly chart data from stream
const extractPlotlyData = (data) => {
  let plotlyData = null

  if (data.actions?.stateDelta?.visualization_result?.plotly_json) {
    plotlyData = data.actions.stateDelta.visualization_result.plotly_json
  } else if (data.actions?.stateDelta?.plotly_json) {
    plotlyData = data.actions.stateDelta.plotly_json
  } else if (data.content?.parts) {
    for (const part of data.content.parts) {
      if (part.functionResponse?.response?.plotly_json) {
        plotlyData = part.functionResponse.response.plotly_json
      }
    }
  }

  return plotlyData
}

// Sanitize plotly data to ensure valid dimensions
export const sanitizePlotlyData = (plotlyData) => {
  if (!plotlyData || typeof plotlyData !== 'object') {
    return plotlyData
  }

  // Create a deep copy to avoid mutating original data
  const sanitizedData = structuredClone(plotlyData)

  // Sanitize layout dimensions
  if (sanitizedData.layout) {
    // Remove invalid width/height or ensure they are numbers
    if (sanitizedData.layout.width !== undefined) {
      const width = Number(sanitizedData.layout.width)
      if (Number.isNaN(width) || width <= 0) {
        delete sanitizedData.layout.width
      } else {
        sanitizedData.layout.width = width
      }
    }

    if (sanitizedData.layout.height !== undefined) {
      const height = Number(sanitizedData.layout.height)
      if (Number.isNaN(height) || height <= 0) {
        delete sanitizedData.layout.height
      } else {
        sanitizedData.layout.height = height
      }
    }
  }

  return sanitizedData
}

// Create chart message object
const createChartMessage = (plotlyData, generatedPlotlyCode, sqlQuery, sqlExecutionResult, tempResponseId) => {
  return {
    id: getRandomId(),
    type: 'Bot',
    label: '',
    responseText: '',
    plotlyJson: sanitizePlotlyData(plotlyData),
    isChart: true,
    feedback: -1,
    aiInfoText: getAIInfoText(),
    generatedPlotlyCode,
    showSourceCode: false,
    sqlQuery,
    sqlExecutionResult,
    tempResponseId
  }
}

export const processStreamData = (data, tempResponseId, accumulatedText, callbacks) => {
  const {setActiveRequestId, setMessages, handleAutoScrollToBottom} = callbacks

  // Handle request started event
  if (data.event === 'request_started' && data.request_id) {
    setActiveRequestId(data.request_id)
  }

  // Skip processing if stream already completed (trailing chunks like coordination_state updates)
  const streamState = initializeStreamState(tempResponseId)
  if (streamState.completed) {
    return accumulatedText
  }

  // Extract all data types
  const newText = extractTextContent(data, accumulatedText)
  const {sqlQuery, sqlExecutionResult} = extractSqlData(data)
  const plotlyData = extractPlotlyData(data)
  const generatedPlotlyCode = data.actions?.stateDelta?.generated_plotly_code || null

  // Handle stream completion (pass newText so completion can use it as fallback)
  handleStreamCompletion(data, callbacks, tempResponseId, accumulatedText + newText)

  // Process text updates
  if (newText) {
    const updatedAccumulatedText = accumulatedText + newText
    setMessages((prevMessages) =>
      prevMessages.map((msg) => (msg.id === tempResponseId ? {...msg, responseText: updatedAccumulatedText} : msg))
    )
    handleAutoScrollToBottom()
    return updatedAccumulatedText
  }

  // Process SQL data updates
  if (sqlQuery || sqlExecutionResult) {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === tempResponseId ? {...msg, sqlQuery, sqlExecutionResult, showSqlQuery: false} : msg
      )
    )
  }

  // Process chart data
  if (plotlyData) {
    const chartMessage = createChartMessage(
      plotlyData,
      generatedPlotlyCode,
      sqlQuery,
      sqlExecutionResult,
      tempResponseId
    )
    setMessages((prevMessages) => [...prevMessages, chartMessage])
    handleAutoScrollToBottom()
  }

  return accumulatedText
}

export const processStream = async (response, tempResponseId, callbacks) => {
  const {
    hideChatLoader,
    setActiveRequestId,
    setIsRequestActive,
    setMessages,
    populateErrorResponse,
    processSSELinesCallback,
    onStreamComplete
  } = callbacks

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let accumulatedText = ''
  let buffer = ''

  try {
    let streamActive = true
    while (streamActive) {
      const {done, value} = await reader.read()

      if (done) {
        hideChatLoader()
        setActiveRequestId(null)
        setIsRequestActive(false)
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === tempResponseId ? {...msg, isStreaming: false, wasCancelled: msg.wasCancelled || false} : msg
          )
        )
        // Call completion callback to reset processing flag
        if (onStreamComplete) {
          onStreamComplete()
        }
        streamActive = false
        continue
      }

      buffer += decoder.decode(value, {stream: true})
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      accumulatedText = processSSELinesCallback(lines, tempResponseId, accumulatedText)
    }
  } catch {
    hideChatLoader()
    setActiveRequestId(null)
    setIsRequestActive(false)
    populateErrorResponse({
      response: ['Oops, something went wrong on our end. Please try again in a few moments.']
    })
    // Call completion callback to reset processing flag even on error
    if (onStreamComplete) {
      onStreamComplete()
    }
  }
}

export const extractCSVFromSQLResults = (sqlExecutionResult) => {
  try {
    if (!sqlExecutionResult.results || !Array.isArray(sqlExecutionResult.results)) {
      return []
    }

    const csvData = sqlExecutionResult.results.map((row) => {
      return row
    })

    return csvData
  } catch {
    return []
  }
}

export const getCSVFilenameFromSQL = (sqlData) => {
  try {
    let baseName = 'query_results'

    if (sqlData.question) {
      baseName = sqlData.question
        .toLowerCase()
        .replaceAll(/[^a-zA-Z0-9\s]/g, '')
        .replaceAll(/\s+/g, '_')
        .substring(0, 30)
    } else if (sqlData.table_name) {
      const tableParts = sqlData.table_name.split('.')
      baseName = tableParts.at(-1).toLowerCase()
    }

    return `${baseName}_${getFormattedTimestamp()}.csv`
  } catch {
    return `sql_results_${getFormattedTimestamp()}.csv`
  }
}

/* Chatbot Helper Functions Documentation:

  CONFIGURATION & CONSTANTS:
  - customStyles: Object containing reusable CSS styles for ordered lists and CSV display elements.
  - MAX_SESSION_STORAGE_SIZE: Constant defining the maximum session storage limit (5MB) for storage management.

  SESSION STORAGE MANAGEMENT:
  - getSessionStorageSizeExceptMessages: Calculates total session storage size excluding the 'messages' key for space optimization.
  - getParsedData: Safely parses JSON strings with error handling, returning null for invalid JSON.
  - setSessionData: Intelligent session storage setter with automatic message trimming when storage limits are exceeded.
  - getSessionData: Simple session storage getter that retrieves raw string data for specified keys.
  - checkMaxLimitIsReached: Determines if adding messages would exceed session storage limits using buffer calculations.

  TEXT FORMATTING & DISPLAY:
  - handleTextFormatting: Advanced text formatter that handles bold markdown (**text**), line breaks, and markdown table detection/rendering.
  - formatMarkdownTable: Converts markdown table syntax into fully styled HTML table elements with responsive design.
  - detectAndFormatTables: Detects markdown tables in text using regex and splits content into text/table sections.
  - getFormattedAIInfoText: Formats AI disclaimer text with FAQ link detection and proper React element wrapping.
  - getFormattedSources: Parses and formats source references with table names into structured numbered lists.
  - getEllipsis: Truncates text to specified length with ellipsis for UI display optimization.

  UTILITY FUNCTIONS:
  - getRandomId: Generates cryptographically secure UUIDs with optional prefix for unique element identification.
  - getFormattedTimestamp: Creates formatted timestamp strings (MM_DD_YYYY_hh_mm_ssA) for file naming and exports.
  - getFormattedName: Processes comma-separated names and returns formatted display names (handles "Last, First" format).
  - shouldDisplayInfoText: Logic controller for warning banner visibility based on prompts, storage limits, and user preferences.
  - shouldDisableButtons: Determines button disabled state based on prompts presence and loading states.

  CHART & VISUALIZATION:
  - getChartColors: Applies consistent color schemes to Plotly charts based on chart type (scatter, pie, etc.).
  - Chart color palette includes 10 predefined colors for consistent branding across visualizations.

  GUIDING PROMPTS & CONTENT:
  - getGuidingPrompts: Returns combined array of general and data inquiry prompts for chat initialization.
  - getGeneralInquiryGuidingPrompts: Generates program-specific (HealthPrism/PeoplePrism) general help prompts with welcome messages.
  - getDataInquiryGuidingPrompts: Creates program-specific data exploration prompts with example queries.
  - getAIInfoText: Returns standard AI disclaimer text for transparency and user awareness.

  API RESPONSE GENERATORS:
  - getAPIFailureResponse: Creates standardized error response objects for general API failures.
  - getAPIInterruptedResponse: Generates response objects for interrupted/cancelled requests.
  - generateAPIResponse: Factory function that returns appropriate error responses based on interruption status.

  STREAMING & REAL-TIME PROCESSING:
  - processSSELines: Parses Server-Sent Event lines and extracts JSON data with error handling for malformed data.
  - processStreamData: Main stream processor that handles partial content, SQL queries, chart data, and message updates.
  - processStream: Complete stream management including reader setup, buffer handling, and cleanup on completion/errors.

  DATA EXTRACTION & EXPORT:
  - extractCSVFromSQLResults: Safely extracts tabular data from SQL execution results for CSV generation.
  - getCSVFilenameFromSQL: Generates descriptive CSV filenames based on query content, table names, or default naming.

  INTEGRATION NOTES:
  - All functions are designed to work with React components and hooks architecture.
  - Session storage functions include intelligent memory management to prevent browser limits.
  - Stream processing functions integrate with real-time chat message updates.
  - Text formatting supports both simple markdown and complex table rendering.
  - Error handling is comprehensive with fallbacks for all parsing and processing operations.
  - Functions support both HealthPrism and PeoplePrism applications through dynamic content generation.
*/
